import type { ShopifyGqlIdType } from "@intelligems/ig-types";
// The GID promotion primitive lives in ig-types so the Agentic Logic INGRESS
// Zod schemas can promote at parse time — ig-types cannot import ig-utils, the
// dependency arrow runs the other way. Duplicating it here is the alternative,
// and two copies of an id-canonicalization regex is exactly the drift that
// produced the bug this delegation exists to prevent.
//
// It is reached by the DEEP SUBPATH, never through the `@intelligems/ig-types`
// barrel. This module ships inside a Shopify checkout UI extension — a
// sandboxed worker with a 64 KB compressed ceiling — via the `ig-utils`
// barrel. A value import of the ig-types barrel drags a 272-line `export *`
// and `zod/v4` in with it (the barrel's schema modules build Zod objects at
// module scope, so no amount of tree-shaking drops them) and the extension
// stops building. `shopify-gid-primitive` is the zod-free leaf: type-only
// imports, nothing else. See
// `extensions/checkout-ui/__tests__/bundle-size.spec.ts`.
import {
  toShopifyGqlId,
  toShopifyGqlIdIfBare,
} from "@intelligems/ig-types/schemas/shopify-gid-primitive";

export class ShopifyId {
  public readonly id: string;
  public readonly gid: string;
  public readonly type: ShopifyGqlIdType;

  constructor(options: { type: ShopifyGqlIdType; unknownId: string }) {
    this.type = options.type;
    if (options.unknownId.includes("gid")) {
      this.id = ShopifyId.fromGqlId(options.unknownId);
      this.gid = options.unknownId;
    } else {
      this.id = options.unknownId;
      this.gid = ShopifyId.toGqlId(options.unknownId, options.type);
    }
  }

  public toString() {
    return this.gid;
  }

  static toGqlId(id: string, type: ShopifyGqlIdType): string {
    return toShopifyGqlId(id, type);
  }

  /**
   * Promotes a bare numeric id to GID form, and leaves everything else alone.
   *
   * The primitive itself lives in `@intelligems/ig-types`
   * (`src/schemas/shopify-gid-primitive.ts`) so the ingress Zod schemas can
   * promote at parse time — `ig-types` cannot import `ig-utils`, the
   * dependency arrow runs the other way. This class API is the same function, unchanged; see
   * `toShopifyGqlIdIfBare` for why the guard exists.
   */
  static toGqlIdIfBare(id: string, type: ShopifyGqlIdType): string {
    return toShopifyGqlIdIfBare(id, type);
  }

  static fromGqlId(gqlId: string): string {
    const split = gqlId.split("/");
    const toRet = split.pop();
    if (toRet) {
      return toRet;
    }
    return gqlId;
  }
}

export class ShopifyMetafieldId extends ShopifyId {
  constructor(unknownId: string) {
    super({ unknownId, type: "Metafield" });
  }

  static toGqlId(id: string): string {
    if (id.includes("gid")) {
      return id;
    }
    return `gid://shopify/Metafield/${id}`;
  }

  /** {@link ShopifyId.toGqlIdIfBare}, pinned to `Metafield`. */
  static toGqlIdIfBare(id: string): string {
    return ShopifyId.toGqlIdIfBare(id, "Metafield");
  }
}

export class ShopifyProductId extends ShopifyId {
  constructor(unknownId: string) {
    super({ unknownId, type: "Product" });
  }

  static toGqlId(id: string): string {
    if (id.includes("gid")) {
      return id;
    }
    return `gid://shopify/Product/${id}`;
  }

  /** {@link ShopifyId.toGqlIdIfBare}, pinned to `Product`. */
  static toGqlIdIfBare(id: string): string {
    return ShopifyId.toGqlIdIfBare(id, "Product");
  }
}

export class ShopifyVariantId extends ShopifyId {
  constructor(unknownId: string) {
    super({ unknownId, type: "ProductVariant" });
  }

  static toGqlId(id: string): string {
    if (id.includes("gid")) {
      return id;
    }
    return `gid://shopify/ProductVariant/${id}`;
  }

  /** {@link ShopifyId.toGqlIdIfBare}, pinned to `ProductVariant`. */
  static toGqlIdIfBare(id: string): string {
    return ShopifyId.toGqlIdIfBare(id, "ProductVariant");
  }
}

export class ShopifyCartTransformId extends ShopifyId {
  constructor(unknownId: string) {
    super({ unknownId, type: "CartTransform" });
  }

  static toGqlId(id: string): string {
    if (id.includes("gid")) {
      return id;
    }
    return `gid://shopify/CartTransform/${id}`;
  }

  /** {@link ShopifyId.toGqlIdIfBare}, pinned to `CartTransform`. */
  static toGqlIdIfBare(id: string): string {
    return ShopifyId.toGqlIdIfBare(id, "CartTransform");
  }
}

export class ShopifyDiscountAutomaticNodeId extends ShopifyId {
  constructor(unknownId: string) {
    super({ unknownId, type: "DiscountAutomaticNode" });
  }

  static toGqlId(id: string): string {
    if (id.includes("gid")) {
      return id;
    }
    return `gid://shopify/DiscountAutomaticNode/${id}`;
  }

  /** {@link ShopifyId.toGqlIdIfBare}, pinned to `DiscountAutomaticNode`. */
  static toGqlIdIfBare(id: string): string {
    return ShopifyId.toGqlIdIfBare(id, "DiscountAutomaticNode");
  }
}

export class ShopifyCollectionId extends ShopifyId {
  constructor(unknownId: string) {
    super({ unknownId, type: "Collection" });
  }

  /** {@link ShopifyId.toGqlId}, pinned to `Collection`. */
  static toGqlId(id: string): string {
    return ShopifyId.toGqlId(id, "Collection");
  }

  /** {@link ShopifyId.toGqlIdIfBare}, pinned to `Collection`. */
  static toGqlIdIfBare(id: string): string {
    return ShopifyId.toGqlIdIfBare(id, "Collection");
  }
}
